import React from "react";
import {
  Menu,
  Group,
  Center,
  Burger,
  Container,
  Image,
  NavLink,
} from "@mantine/core";
import { useDisclosure, useViewportSize } from "@mantine/hooks";
import { IconChevronDown, IconLogin, IconUserPlus } from "@tabler/icons-react";
import { FaHome, FaBolt, FaDiscord } from "react-icons/fa"; // Importing the required icons
import { PAGES } from "./docsIndex";

interface HeaderMenuProps {
  is_logged_in: boolean;
}

const links = [
  // Define links here as needed
];

const DocsHeader: React.FC<HeaderMenuProps> = ({ is_logged_in }) => {
  const { width } = useViewportSize();
  const [opened, { toggle }] = useDisclosure(false);
  const isMobile = width < 768;

  const items = links.map((link) => {
    const menuItems = [
      <NavLink
        key="docs-home"
        href="/docs"
        label="Documentation Home"
        childrenOffset={12}
        style={hoverStyles}
      />,
      ...link.links?.map((item) => (
        <NavLink
          key={item.link}
          href={item.link}
          label={item.label}
          childrenOffset={12}
          style={hoverStyles}
        >
          {item.articles.map((article) => (
            <NavLink
              key={article.link}
              href={article.link}
              label={article.label}
              style={hoverStyles}
            />
          ))}
        </NavLink>
      )),
    ];

    return (
      <Menu
        key={link.label}
        trigger="hover"
        transitionProps={{ exitDuration: 0 }}
        withinPortal
      >
        <Menu.Target>
          <a
            href={link.link}
            style={{ ...linkStyles, ...hoverStyles }}
            onClick={(e) => e.preventDefault()}
          >
            <Center>
              <span style={linkLabelStyles}>{link.label}</span>
              <IconChevronDown size="0.9rem" stroke={1.5} />
            </Center>
          </a>
        </Menu.Target>
        <Menu.Dropdown>{menuItems}</Menu.Dropdown>
      </Menu>
    );
  });

  const iconStyle = {
    marginRight: "0.5rem",
    verticalAlign: "middle",
  };

  const nostrImageStyles = {
    height: "1.25rem",
    width: "1.25rem",
    marginRight: "0.33rem",
    opacity: 0.8,
    verticalAlign: "middle",
  };

  const authLinks = is_logged_in ? (
    <>
      <a href="/" style={{ ...linkStyles, ...hoverStyles }}>
        <FaHome style={iconStyle} /> Home
      </a>
      <a href="/nodes" style={{ ...linkStyles, ...hoverStyles }}>
        <FaBolt style={iconStyle} /> My Nodes
      </a>
      <a
        href="https://discord.gg/ZrT25cC44s"
        target="_blank"
        style={{ ...linkStyles, ...hoverStyles }}
      >
        <FaDiscord style={iconStyle} /> Discord
      </a>
      <a
        href="https://your-nostr-link-here"
        target="_blank"
        style={{ ...linkStyles, ...hoverStyles }}
      >
        <img
          src="https://daxwktpz4hkwd.cloudfront.net/logos/nostr_logo_wht_lg.png"
          alt="Nostr"
          style={nostrImageStyles}
        />
        Nostr
      </a>
    </>
  ) : (
    <>
      <a href="/login" style={{ ...linkStyles, ...hoverStyles }}>
        <IconLogin
          size="1rem"
          style={{ ...iconStyle, marginRight: "0.5rem" }}
        />
        Sign In
      </a>
      <a href="/create-account" style={{ ...linkStyles, ...hoverStyles }}>
        <IconUserPlus
          size="1rem"
          style={{ ...iconStyle, marginRight: "0.5rem" }}
        />
        Sign Up
      </a>
    </>
  );

  return (
    <header style={headerStyles}>
      <Container size="md" style={{ padding: 0 }}>
        <div style={innerStyles}>
          <a href="/" style={logoLinkStyles}>
            <Image
              h={"36px"}
              fit="contain"
              src={
                "https://daxwktpz4hkwd.cloudfront.net/logos/rizful-logo-mobile.png"
              }
              alt="Rizful logo"
            />
          </a>
          {isMobile ? (
            <Menu
              opened={opened}
              onClose={() => toggle()}
              onOpen={() => toggle()}
              withinPortal
              transitionProps={{ transition: "scale-y", duration: 150 }}
            >
              <Menu.Target>
                <Burger opened={opened} onClick={toggle} size="sm" />
              </Menu.Target>
              <Menu.Dropdown>
                {authLinks}
                {items}
              </Menu.Dropdown>
            </Menu>
          ) : (
            <Group gap={5}>
              {authLinks}
              {items}
            </Group>
          )}
        </div>
      </Container>
    </header>
  );
};

const headerStyles = {
  height: "56px",
  marginBottom: "10px",
  backgroundColor: "var(--mantine-color-body)",
  borderBottom:
    "1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))",
};

const innerStyles = {
  height: "56px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const logoLinkStyles = {
  display: "flex",
  alignItems: "center",
  marginLeft: "0", // Ensures the logo aligns with the left edge
  paddingLeft: "0", // Removes any default padding
  textDecoration: "none",
};

const linkStyles = {
  display: "flex", // Updated to flex
  alignItems: "center", // Center items vertically
  lineHeight: 1,
  padding: "8px 12px",
  borderRadius: "var(--mantine-radius-sm)",
  textDecoration: "none",
  color: "light-dark(var(--mantine-color-gray-7), var(--mantine-color-dark-0))",
  fontSize: "var(--mantine-font-size-sm)",
  fontWeight: 500,
  transition: "background-color 0.3s, color 0.3s",
};

const hoverStyles = {
  ":hover": {
    backgroundColor: "var(--mantine-color-gray-1)",
    color: "var(--mantine-color-blue-6)",
    cursor: "pointer",
  },
};

const linkLabelStyles = {
  marginRight: "5px",
};

export default DocsHeader;
